import { Directive, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  ActivityFileTypeEnum,
  OrganizationInterface,
  ReviewStatInterface,
} from '@sport-activities/sdk';
import { Subscription } from 'rxjs';
import { Logger } from '~/core/logger.service';

import { BackLinkInterface } from '~/checkout/interfaces/back-link.interface';
import { urlToBackLink } from '~/core/helpers/url/url-to-back-link/url-to-back-link.helper';
import { Activity } from '~/core/model/activity.model';
import { Timeslot } from '~/core/model/timeslot.model';
import { CustomHeadTagService } from '~/core/services/custom-head-tag/custom-head-tag.service';
import { OpenGraphPropertyEnum } from '~/core/services/open-graph/enums/open-graph-property.enum';
import { OpenGraphService } from '~/core/services/open-graph/open-graph.service';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { RouterHistoryService } from '~/core/services/router-history/router-history.service';
import { SportsService } from '~/core/services/sports/sports.service';
import { TitleService } from '~/core/services/title/title.service';
import { ActivityMediaResolutionEnum } from '~/organizer/shared/enums/media-resolution.enum';
import { ToggleService } from '~/toggles/services/toggle.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';

@Directive()
export abstract class AbstractActivityPages implements OnInit, OnDestroy {
  private readonly subscriptions = new Subscription();
  public readonly toggleNameEnum = ToggleNameEnum;
  public activity: Activity;
  public decatClubUrl: string;
  public isPreview: boolean;
  public organization: OrganizationInterface;
  public organizationReviewsStats: ReviewStatInterface;
  public timeslots: Timeslot[];

  constructor(
    protected readonly activatedRoute: ActivatedRoute,
    protected readonly titleService: TitleService,
    protected readonly routerHistory: RouterHistoryService,
    protected readonly openGraphService: OpenGraphService,
    protected readonly sportsService: SportsService,
    protected readonly redirectionService: RedirectionService,
    protected readonly toggleService: ToggleService,
    protected readonly customHeadTag: CustomHeadTagService,
    protected readonly organizerSharedActivityService: OrganizerSharedActivityService,
    protected readonly logger: Logger
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.openGraphService.removeMeta();
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.activatedRoute.data.subscribe(
        ({ activityDetailData, localizeRouter }) => {
          this.activity = activityDetailData?.activity;
          this.decatClubUrl = this.toggleService.getVariantString(
            this.toggleNameEnum.DecatClubUrl,
            null
          );
          this.organization = activityDetailData?.organization;
          this.organizationReviewsStats =
            activityDetailData?.organizationReviewsStats ?? {};
          this.timeslots = activityDetailData?.timeslots.sort((a, b) =>
            a.startDate >= b.startDate ? 1 : -1
          );

          this.isPreview = activityDetailData?.isPreview;

          // If we don't display full activities, filter on isBookable
          if (
            !this.toggleService.isEnabled(ToggleNameEnum.ShowFullActivities)
          ) {
            this.timeslots = this.timeslots.filter((t) =>
              t.isBookable(this.activity?.bookableUntilTheEnd)
            );
          }

          this.setTitle();
          this.handleRedirections(activityDetailData, localizeRouter?.path);
          this.constructMicrodata();
        }
      )
    );

    this.setContentMeta();

    this.openGraphService.setMeta([
      {
        property: OpenGraphPropertyEnum.TITLE,
        content: `${
          this.sportsService.getSport(this.activity?.sports?.[0])?.name
        } - ${this.activity?.name}`,
      },
      {
        property: OpenGraphPropertyEnum.DESCRIPTION,
        content: this.activity?.fullAddress,
      },
      {
        property: OpenGraphPropertyEnum.IMAGE,
        content:
          this.activity?.getMediaUrlByType(
            'MAIN_MEDIA' as ActivityFileTypeEnum,
            ActivityMediaResolutionEnum.CARD
          ) || this.activity?.picture,
      },
    ]);
  }

  public get backLink(): BackLinkInterface {
    return urlToBackLink(this.routerHistory.previous || '/');
  }

  protected constructMicrodata(): void {
    // Can be override in pages if needed
  }
  protected handleRedirections(
    activityDetailData: { [key: string]: Activity },
    path: string
  ): void {
    // Can be override in pages if needed
  }
  protected setContentMeta(): void {
    // Can be override in pages if needed
  }
  protected setTitle(): void {
    // Can be override in pages if needed
  }
}
